import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { keyServices, contactInfo } from "../data";
import ReviewSlider from "../components/ReviewSlider";
import ContactForm from "../components/ContactForm";

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Hent forside-data fra CMS via by-slug endpointet
    fetch("https://api.dev.auxo.dk/public/by-slug/da/forside")
      .then((res) => {
        if (!res.ok) throw new Error("Kunne ikke hente forside-data");
        return res.json();
      })
      .then((data) => {
        setPageData(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Fejl ved hentning af forside-data:", err);
        setLoading(false);
      });
  }, []);

  // Robust helper til at udtrække tekstværdier (håndterer både rå strenge og lokaliserede objekter)
  const getVal = (field, defaultVal = "") => {
    if (!field) return defaultVal;
    if (typeof field === "object") {
      return field.da || Object.values(field)[0] || defaultVal;
    }
    return field;
  };

  // Cloudflare image resizing helper
  const getResizedUrl = (url, width = 1200) => {
    if (!url) return "";
    const imgUrl = Array.isArray(url) ? url[0] : url;
    if (!imgUrl.startsWith("https://assets.auxodev.dk")) return imgUrl;
    return imgUrl.replace(
      "https://assets.auxodev.dk/",
      `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
    );
  };

  // Sæt metadata dynamisk
  useEffect(() => {
    if (pageData) {
      document.title = getVal(pageData.meta_title, "Grønhøjauto v/Mads Kallerup");
      
      // Opdater meta description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement("meta");
        metaDesc.setAttribute("name", "description");
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute(
        "content",
        getVal(
          pageData.meta_description,
          "Professionelt og lokalt autoværksted i Hurup Thy v/Mads Kallerup. Vi tilbyder service, reparation, dæk, hjul og synsklargøring."
        )
      );
    } else {
      document.title = "Grønhøjauto v/Mads Kallerup";
    }
  }, [pageData]);

  const heroImg = getResizedUrl(
    pageData?.hero_image || "https://assets.auxodev.dk/assets/45330f91-18ad-4301-b0b9-f03458bae5a3/gronhojauto-hero.jpg",
    1600
  );

  const mechanicImg = getResizedUrl(
    "https://assets.auxodev.dk/assets/45330f91-18ad-4301-b0b9-f03458bae5a3/att-za8lyugblnuf9e6umg0bqtcan5w99-gxxjwkxd0vw4k_1a11b2f6.jpg",
    600
  );

  return (
    <div className="pt-[72px] min-[1200px]:pt-[115px]">
      {/* Hero Sektion */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden bg-stone-900 text-white">
        {/* Hero baggrundsbillede */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroImg}
            alt="Grønhøjauto værksted"
            className="h-full w-full object-cover object-center opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-900/50 to-stone-950/30" />
        </div>

        <div className="relative z-10 mx-auto max-w-5xl px-6 py-20 text-center space-y-6">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-sage/20 px-4 py-1.5 text-xs font-bold tracking-wider text-brand-sage-dark uppercase border border-brand-sage/30">
            <i className="fa-solid fa-wrench text-[10px] text-brand-sage-dark" /> Din lokale mekaniker i Hurup Thy
          </span>
          
          <h1 className="text-4xl font-black tracking-tight sm:text-5xl md:text-6xl text-white leading-tight">
            {getVal(pageData?.title, "Grønhøjauto")}
          </h1>
          
          <p className="mx-auto max-w-2xl text-lg text-stone-200 md:text-xl font-medium leading-relaxed">
            {getVal(pageData?.subtitle, "Dit lokale autoværksted i Hurup Thy – vi klarer det hele med et smil")}
          </p>

          <div className="pt-4 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/kontakt"
              state={{ subject: "Book tid" }}
              className="rounded-xl bg-brand-orange px-8 py-4 text-base font-bold text-white shadow-lg shadow-brand-orange/20 transition duration-200 hover:bg-brand-orange-dark hover:scale-102 cursor-pointer"
            >
              Book tid nu
            </Link>
            <Link
              to="/kontakt"
              className="rounded-xl bg-brand-sage px-8 py-4 text-base font-bold text-white shadow-lg shadow-brand-sage/20 transition duration-200 hover:bg-brand-sage-dark hover:scale-102 cursor-pointer"
            >
              Kontakt værkstedet
            </Link>
          </div>

          {/* Hurtige USP'er i Hero */}
          <div className="pt-12 grid grid-cols-2 gap-4 max-w-lg mx-auto sm:grid-cols-3 sm:max-w-2xl text-xs text-stone-300 font-semibold">
            <div className="flex items-center justify-center gap-2 bg-white/5 rounded-lg p-3 border border-white/10">
              <i className="fa-solid fa-circle-check text-brand-orange" />
              <span>Bevarer fabriksgaranti</span>
            </div>
            <div className="flex items-center justify-center gap-2 bg-white/5 rounded-lg p-3 border border-white/10">
              <i className="fa-solid fa-circle-check text-brand-orange" />
              <span>Ærligt arbejde</span>
            </div>
            <div className="flex items-center justify-center gap-2 bg-white/5 rounded-lg p-3 border border-white/10 col-span-2 sm:col-span-1">
              <i className="fa-solid fa-circle-check text-brand-orange" />
              <span>Alle bilmærker</span>
            </div>
          </div>
        </div>
      </section>

      {/* Vigtigste Ydelser Sektion */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs font-bold tracking-widest text-brand-orange uppercase">
              Hvad kan vi hjælpe med?
            </span>
            <h2 className="text-3xl font-black tracking-tight text-brand-stone-dark sm:text-4xl">
              Værkstedets vigtigste ydelser
            </h2>
            <p className="text-stone-600">
              Hos Grønhøjauto kan vi lave det hele. Vi tilbyder professionelt arbejde på alle bilmærker og modeller med moderne udstyr og reservedele af højeste kvalitet.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {keyServices.map((service, idx) => (
              <Link
                key={idx}
                to={`/${service.slug}`}
                className="group flex flex-col justify-between rounded-2xl border border-stone-200 bg-stone-50/50 p-8 transition duration-300 hover:border-brand-green/30 hover:shadow-md cursor-pointer"
              >
                <div className="space-y-4">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-green/10 text-brand-green group-hover:bg-brand-green group-hover:text-white transition duration-300">
                    <i className={`${service.icon} text-xl`} />
                  </div>
                  <h3 className="text-xl font-bold text-brand-stone-dark group-hover:text-brand-green transition duration-300">
                    {service.title}
                  </h3>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
                <div className="mt-6 pt-4 border-t border-stone-100">
                  <span
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-green group-hover:text-brand-orange transition"
                  >
                    Læs mere her <i className="fa-solid fa-arrow-right text-xs" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Tillidssektion */}
      <section className="py-20 bg-brand-stone-light border-y border-stone-200/60">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-center">
            <div className="space-y-6">
              <span className="text-xs font-bold tracking-widest text-brand-orange uppercase">
                Hvorfor vælge os?
              </span>
              <h2 className="text-3xl font-black tracking-tight text-brand-stone-dark sm:text-4xl leading-tight">
                Et trygt valg for dig og din bil
              </h2>
              <p className="text-stone-600 leading-relaxed">
                Vi ved, at det handler om tillid, når du afleverer din bil på et værksted. Derfor lægger vi stor vægt på personlig service, fuld gennemsigtighed og uovertruffen kvalitet i alt, hvad vi gør.
              </p>

              <div className="space-y-5 pt-4">
                <div className="flex gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-sage/20 text-brand-sage-dark">
                    <i className="fa-solid fa-user-check" />
                  </div>
                  <div>
                    <h4 className="font-bold text-brand-stone-dark">Personlig service</h4>
                    <p className="text-sm text-stone-600 mt-1">
                      Du taler altid direkte med Mads Kallerup, der udfører arbejdet. Det sikrer, at der ikke sker misforståelser undervejs.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-sage/20 text-brand-sage-dark">
                    <i className="fa-solid fa-handshake" />
                  </div>
                  <div>
                    <h4 className="font-bold text-brand-stone-dark">Ærligt arbejde & faste priser</h4>
                    <p className="text-sm text-stone-600 mt-1">
                      Vi ringer altid med a fast pris, før vi går i gang med uforudsete reparationer. Ingen skjulte gebyrer eller ubehagelige overraskelser.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-sage/20 text-brand-sage-dark">
                    <i className="fa-solid fa-award" />
                  </div>
                  <div>
                    <h4 className="font-bold text-brand-stone-dark">Bred erfaring & høj kvalitet</h4>
                    <p className="text-sm text-stone-600 mt-1">
                      Vi arbejder med moderne testudstyr og anvender kun godkendte reservedele, så din bil bevarer sin fabriksgaranti.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tillids-grafik/billede */}
            <div className="relative rounded-2xl overflow-hidden border border-stone-200 shadow-lg aspect-4/3 lg:aspect-square">
              <img
                src={getResizedUrl("https://assets.auxodev.dk/assets/45330f91-18ad-4301-b0b9-f03458bae5a3/img-5708.jpeg", 800)}
                alt="Kundedialog hos Grønhøjauto"
                className="h-full w-full object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-md rounded-xl p-5 border border-stone-100 shadow-md">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-green text-white">
                    <i className="fa-solid fa-shield-heart" />
                  </div>
                  <div>
                    <p className="text-xs text-stone-500 font-bold uppercase tracking-wider">Vores garanti</p>
                    <p className="text-sm font-black text-brand-stone-dark">100% tilfredshed eller pengene tilbage</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Kort "Om Grønhøjauto v/Mads Kallerup" */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 items-center">
            <div className="lg:col-span-5 relative">
              <div className="aspect-3/4 rounded-2xl overflow-hidden border border-stone-200 shadow-lg">
                <img
                  src={mechanicImg}
                  alt="Mads Kallerup - Grønhøjauto"
                  className="h-full w-full object-cover object-center"
                />
              </div>
              {/* Lille badge */}
              <div className="absolute -bottom-6 -right-6 bg-brand-green text-white rounded-2xl p-6 border border-brand-green-light shadow-xl hidden sm:block">
                <p className="text-3xl font-black">Mads K.</p>
                <p className="text-xs text-stone-200 font-bold uppercase tracking-widest mt-1">Ejer & Mekaniker</p>
              </div>
            </div>

            <div className="lg:col-span-7 space-y-6 lg:pl-6">
              <span className="text-xs font-bold tracking-widest text-brand-orange uppercase">
                Mød din mekaniker
              </span>
              <h2 className="text-3xl font-black tracking-tight text-brand-stone-dark sm:text-4xl">
                Om Grønhøjauto v/Mads Kallerup
              </h2>
              <p className="text-stone-600 leading-relaxed">
                Grønhøjauto drives af Mads Kallerup, som er uddannet mekaniker med mange års erfaring inden for alle bilmærker. Værkstedet blev grundlagt med en vision om at skabe et lokalt alternativ til de store, upersonlige værkstedskæder. Her er der plads til den enkelte kunde, og vi tager os tid til at lytte og forklare tingene i øjenhøjde.
              </p>
              <p className="text-stone-600 leading-relaxed">
                Når du overlader din bil til Grønhøjauto, kan du være sikker på, at der kun bliver udført det arbejde, vi har aftalt. Skulle vi undervejs opdage andre ting, der trænger til opmærksomhed, ringer vi altid til dig først med et prisoverslag. Det kalder vi ærligt arbejde.
              </p>
              <div className="pt-4">
                <Link
                  to="/vaerkstedet"
                  className="inline-flex items-center gap-2 rounded-xl bg-brand-green px-6 py-3 text-sm font-bold text-white shadow-md transition hover:bg-brand-green-light cursor-pointer"
                >
                  Læs mere om værkstedet
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Anmeldelser Sektion */}
      <section className="py-20 bg-brand-stone-light border-t border-stone-200/60 overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 mb-12 text-center space-y-4">
          <span className="text-xs font-bold tracking-widest text-brand-orange uppercase">
            Glade kunder
          </span>
          <h2 className="text-3xl font-black tracking-tight text-brand-stone-dark sm:text-4xl">
            Hvad siger vores kunder?
          </h2>
          <p className="text-stone-600 max-w-2xl mx-auto">
            Vi lever af glade og tilfredse kunder i lokalområdet. Her er et udvalg af de anmeldelser, vi har modtaget fra lokale bilejere.
          </p>
        </div>
        
        {/* Infinite review slider */}
        <ReviewSlider />
      </section>

      {/* Kontaktsektion */}
      <section className="py-20 bg-white border-t border-stone-200/60">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            {/* Kontakt info */}
            <div className="lg:col-span-5 space-y-8">
              <div className="space-y-4">
                <span className="text-xs font-bold tracking-widest text-brand-orange uppercase">
                  Kontakt os i dag
                </span>
                <h2 className="text-3xl font-black tracking-tight text-brand-stone-dark sm:text-4xl">
                  Klar til at hjælpe dig videre
                </h2>
                <p className="text-stone-600">
                  Har du spørgsmål, brug for et uforpligtende tilbud eller vil du booke en tid? Ring, send en mail eller udfyld formularen, så vender vi hurtigt tilbage.
                </p>
              </div>

              <div className="space-y-4 text-sm text-stone-600">
                <div className="flex items-start gap-4 rounded-xl bg-stone-50 p-4 border border-stone-100">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-green/10 text-brand-green">
                    <i className="fa-solid fa-phone" />
                  </div>
                  <div>
                    <h4 className="font-bold text-brand-stone-dark">Telefon</h4>
                    <p className="mt-1 text-base font-black text-brand-green whitespace-nowrap">
                      <a href={`tel:+45${contactInfo.phone.replace(/\s/g, "")}`} className="hover:text-brand-orange transition">{contactInfo.phone}</a>
                    </p>
                    <p className="text-xs text-stone-400 mt-0.5">Ring og få en uformel snak</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 rounded-xl bg-stone-50 p-4 border border-stone-100">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-green/10 text-brand-green">
                    <i className="fa-solid fa-envelope" />
                  </div>
                  <div>
                    <h4 className="font-bold text-brand-stone-dark">E-mail</h4>
                    <p className="mt-1 text-base font-bold text-brand-stone-dark whitespace-nowrap">
                      <a href={`mailto:${contactInfo.email}`} className="hover:text-brand-orange transition">{contactInfo.email}</a>
                    </p>
                    <p className="text-xs text-stone-400 mt-0.5">Vi svarer typisk inden for et par timer</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 rounded-xl bg-stone-50 p-4 border border-stone-100">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-green/10 text-brand-green">
                    <i className="fa-solid fa-location-dot" />
                  </div>
                  <div>
                    <h4 className="font-bold text-brand-stone-dark">Adresse</h4>
                    <p className="mt-1 text-stone-700">
                      {contactInfo.address}
                      <br />
                      {contactInfo.postalCode}
                    </p>
                  </div>
                </div>
              </div>

              {/* Åbningstider */}
              <div className="rounded-xl border border-stone-200 bg-stone-50/50 p-6">
                <h4 className="font-bold text-brand-stone-dark mb-3 flex items-center gap-2">
                  <i className="fa-solid fa-clock text-brand-orange" /> Åbningstider på værkstedet
                </h4>
                <ul className="space-y-2 text-sm text-stone-600">
                  {contactInfo.hours.map((h, idx) => (
                    <li key={idx} className="flex justify-between border-b border-stone-200/40 pb-1.5 last:border-0 last:pb-0">
                      <span>{h.days}:</span>
                      <span className="font-bold text-brand-stone-dark">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Formular */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
